import config from "../../config";

const request = async (token, path, options = {}) => {
  const response = await fetch(`${config.devurl}${path}`, {
    method: options.method || "GET",
    credentials: "same-origin",
    headers: {
      Authorization: token,
      ...(options.body ? { "Content-Type": "application/json" } : {}),
    },
    ...(options.body ? { body: JSON.stringify(options.body) } : {}),
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload.error || payload.message || "Transportation request failed.");
    error.status = response.status;
    error.code = payload.code;
    error.issues = payload.issues || payload.readiness?.issues || [];
    error.readiness = payload.readiness;
    error.details = payload.details;
    throw error;
  }
  return payload;
};

export const buildStopsPath = (filters = {}) => {
  const query = new URLSearchParams();
  ["page", "limit", "search", "status", "zone", "sortBy", "sortOrder"].forEach((key) => {
    const value = filters[key];
    if (value !== undefined && value !== null && value !== "") query.set(key, value);
  });
  const suffix = query.toString();
  return `/transportation/stops${suffix ? `?${suffix}` : ""}`;
};

export const buildVendorsPath = (filters = {}) => {
  const query = new URLSearchParams();
  ["page", "limit", "search", "status", "sortBy", "sortOrder"].forEach((key) => {
    const value = filters[key];
    if (value !== undefined && value !== null && value !== "") query.set(key, value);
  });
  const suffix = query.toString();
  return `/transportation/vendors${suffix ? `?${suffix}` : ""}`;
};

export const buildVehiclesPath = (filters = {}) => {
  const query = new URLSearchParams();
  [
    "page",
    "limit",
    "search",
    "status",
    "operationalStatus",
    "ownershipType",
    "vehicleType",
    "vendor",
    "sortBy",
    "sortOrder",
  ].forEach((key) => {
    const value = filters[key];
    if (value !== undefined && value !== null && value !== "") query.set(key, value);
  });
  const suffix = query.toString();
  return `/transportation/vehicles${suffix ? `?${suffix}` : ""}`;
};

export const buildStaffPath = (filters = {}) => {
  const query = new URLSearchParams();
  ["page", "limit", "search", "status", "operationalStatus", "affiliationType", "role", "vendor", "sortBy", "sortOrder"].forEach((key) => {
    const value = filters[key];
    if (value !== undefined && value !== null && value !== "") query.set(key, value);
  });
  const suffix = query.toString();
  return `/transportation/staff${suffix ? `?${suffix}` : ""}`;
};

export const buildRoutesPath = (filters = {}) => {
  const query = new URLSearchParams();
  ["page", "limit", "search", "status", "direction", "routeType", "operatingDay", "defaultVendor", "defaultVehicle", "sortBy", "sortOrder"].forEach((key) => {
    const value = filters[key];
    if (value !== undefined && value !== null && value !== "") query.set(key, value);
  });
  const suffix = query.toString();
  return `/transportation/routes${suffix ? `?${suffix}` : ""}`;
};

export const buildTripsPath = (filters = {}) => {
  const query = new URLSearchParams();
  ["page", "limit", "serviceDate", "dateFrom", "dateTo", "route", "direction", "status", "readiness", "missingResource", "vehicle", "driver", "sortBy", "sortOrder"].forEach((key) => {
    const value = filters[key];
    if (value !== undefined && value !== null && value !== "") query.set(key, value);
  });
  const suffix = query.toString();
  return `/transportation/trips${suffix ? `?${suffix}` : ""}`;
};

export const buildResourceSchedulesPath = (filters = {}) => {
  const query = new URLSearchParams();
  ["page", "limit", "route", "status"].forEach((key) => {
    const value = filters[key];
    if (value !== undefined && value !== null && value !== "") query.set(key, value);
  });
  const suffix = query.toString();
  return `/transportation/resource-schedules${suffix ? `?${suffix}` : ""}`;
};

export const buildAvailableResourcesPath = (tripId, filters = {}) => {
  const query = new URLSearchParams();
  ["type", "page", "limit", "search"].forEach((key) => {
    const value = filters[key];
    if (value !== undefined && value !== null && value !== "") query.set(key, value);
  });
  return `/transportation/trips/${tripId}/available-resources?${query.toString()}`;
};

export const buildTripRosterPath = (tripId, filters = {}) => {
  const query = new URLSearchParams();
  ["page", "limit", "stop", "sortBy", "sortOrder"].forEach((key) => {
    const value = filters[key];
    if (value !== undefined && value !== null && value !== "") query.set(key, value);
  });
  const suffix = query.toString();
  return `/transportation/trips/${tripId}/roster${suffix ? `?${suffix}` : ""}`;
};

export const buildStudentsPath = (filters = {}) => {
  const query = new URLSearchParams();
  ["page", "limit", "search", "section", "group", "view", "transportationEnabled", "assignmentState", "pickupRoute", "dropRoute"].forEach((key) => {
    const value = filters[key];
    if (value !== undefined && value !== null && value !== "") query.set(key, value);
  });
  const suffix = query.toString();
  return `/transportation/students${suffix ? `?${suffix}` : ""}`;
};

export const transportationService = {
  getOverview: (token) => request(token, "/transportation/overview"),
  getStops: (token, filters) => request(token, buildStopsPath(filters)),
  getStop: (token, stopId) => request(token, `/transportation/stops/${stopId}`),
  createStop: (token, body) => request(token, "/transportation/stops", { method: "POST", body }),
  updateStop: (token, stopId, body) =>
    request(token, `/transportation/stops/${stopId}`, { method: "PATCH", body }),
  updateStopStatus: (token, stopId, status) =>
    request(token, `/transportation/stops/${stopId}/status`, {
      method: "PATCH",
      body: { status },
    }),
  archiveStop: (token, stopId) =>
    request(token, `/transportation/stops/${stopId}`, { method: "DELETE" }),
  getVendors: (token, filters) => request(token, buildVendorsPath(filters)),
  getVendor: (token, vendorId) => request(token, `/transportation/vendors/${vendorId}`),
  createVendor: (token, body) =>
    request(token, "/transportation/vendors", { method: "POST", body }),
  updateVendor: (token, vendorId, body) =>
    request(token, `/transportation/vendors/${vendorId}`, { method: "PATCH", body }),
  updateVendorStatus: (token, vendorId, status) =>
    request(token, `/transportation/vendors/${vendorId}/status`, {
      method: "PATCH",
      body: { status },
    }),
  archiveVendor: (token, vendorId) =>
    request(token, `/transportation/vendors/${vendorId}`, { method: "DELETE" }),
  getVehicles: (token, filters) => request(token, buildVehiclesPath(filters)),
  getVehicle: (token, vehicleId) => request(token, `/transportation/vehicles/${vehicleId}`),
  createVehicle: (token, body) =>
    request(token, "/transportation/vehicles", { method: "POST", body }),
  updateVehicle: (token, vehicleId, body) =>
    request(token, `/transportation/vehicles/${vehicleId}`, { method: "PATCH", body }),
  updateVehicleStatus: (token, vehicleId, status) =>
    request(token, `/transportation/vehicles/${vehicleId}/status`, {
      method: "PATCH",
      body: { status },
    }),
  archiveVehicle: (token, vehicleId) =>
    request(token, `/transportation/vehicles/${vehicleId}`, { method: "DELETE" }),
  getStaff: (token, filters) => request(token, buildStaffPath(filters)),
  getStaffMember: (token, staffId) => request(token, `/transportation/staff/${staffId}`),
  createStaff: (token, body) => request(token, "/transportation/staff", { method: "POST", body }),
  updateStaff: (token, staffId, body) => request(token, `/transportation/staff/${staffId}`, { method: "PATCH", body }),
  updateStaffStatus: (token, staffId, status) => request(token, `/transportation/staff/${staffId}/status`, { method: "PATCH", body: { status } }),
  archiveStaff: (token, staffId) => request(token, `/transportation/staff/${staffId}`, { method: "DELETE" }),
  getRoutes: (token, filters) => request(token, buildRoutesPath(filters)),
  getRoute: (token, routeId) => request(token, `/transportation/routes/${routeId}`),
  createRoute: (token, body) => request(token, "/transportation/routes", { method: "POST", body }),
  updateRoute: (token, routeId, body) => request(token, `/transportation/routes/${routeId}`, { method: "PATCH", body }),
  updateRouteStatus: (token, routeId, status) => request(token, `/transportation/routes/${routeId}/status`, { method: "PATCH", body: { status } }),
  archiveRoute: (token, routeId) => request(token, `/transportation/routes/${routeId}`, { method: "DELETE" }),
  getResourceSchedules: (token, filters) => request(token, buildResourceSchedulesPath(filters)),
  getResourceSchedule: (token, scheduleId) => request(token, `/transportation/resource-schedules/${scheduleId}`),
  createResourceSchedule: (token, body) => request(token, "/transportation/resource-schedules", { method: "POST", body }),
  updateResourceSchedule: (token, scheduleId, body) => request(token, `/transportation/resource-schedules/${scheduleId}`, { method: "PATCH", body }),
  updateResourceScheduleStatus: (token, scheduleId, status) => request(token, `/transportation/resource-schedules/${scheduleId}/status`, { method: "PATCH", body: { status } }),
  archiveResourceSchedule: (token, scheduleId) => request(token, `/transportation/resource-schedules/${scheduleId}`, { method: "DELETE" }),
  getTrips: (token, filters) => request(token, buildTripsPath(filters)),
  getTrip: (token, tripId) => request(token, `/transportation/trips/${tripId}`),
  generateTrips: (token, serviceDate) =>
    request(token, "/transportation/trips/generate", {
      method: "POST",
      body: { serviceDate },
    }),
  getTripRoster: (token, tripId, filters) =>
    request(token, buildTripRosterPath(tripId, filters)),
  generateTripRoster: (token, tripId) =>
    request(token, `/transportation/trips/${tripId}/roster/generate`, {
      method: "POST",
    }),
  createTrip: (token, body) => request(token, "/transportation/trips", { method: "POST", body }),
  updateTrip: (token, tripId, body) => request(token, `/transportation/trips/${tripId}`, { method: "PATCH", body }),
  getAvailableTripResources: (token, tripId, filters) => request(token, buildAvailableResourcesPath(tripId, filters)),
  updateTripResources: (token, tripId, body) => request(token, `/transportation/trips/${tripId}/resources`, { method: "PATCH", body }),
  startTrip: (token, tripId) => request(token, `/transportation/trips/${tripId}/start`, { method: "POST" }),
  completeTrip: (token, tripId) => request(token, `/transportation/trips/${tripId}/complete`, { method: "POST" }),
  cancelTrip: (token, tripId, body) => request(token, `/transportation/trips/${tripId}/cancel`, { method: "POST", body }),
  getStudents: (token, filters) => request(token, buildStudentsPath(filters)),
  getStudentAssignment: (token, childId) => request(token, `/transportation/students/${childId}/assignment`),
  updateStudentAssignment: (token, childId, body) => request(token, `/transportation/students/${childId}/assignment`, { method: "PUT", body }),
  archiveStudentAssignment: (token, childId) => request(token, `/transportation/students/${childId}/assignment`, { method: "DELETE" }),
  bulkUpdateStudentAssignments: (token, body) => request(token, "/transportation/students/bulk-assignment", { method: "POST", body }),
  getSections: (token) => request(token, "/sections/viewAll"),
  getGroups: (token) => request(token, "/groups/viewAll"),
  getSettings: (token) => request(token, "/transportation/settings"),
  updateSettings: (token, body) =>
    request(token, "/transportation/settings", { method: "PUT", body }),
  getSettingsCoordinators: (token) => request(token, "/transportation/settings/coordinators"),
};
